import React, { useState, useEffect } from "react";
import { Label, TextInput, ToggleSwitch } from "flowbite-react";
import { Clock, MessageCircle, Smartphone } from "lucide-react";
import { toast } from "react-hot-toast";
import HeaderWithIcon from "../../components/common/HeaderWithIcon";
import LeftToRightPageWrapper from "../../components/common/LeftToRightPageWrapper ";
import { settingsApi } from "../../api/settings";
import { useAuth } from "../../context/AuthContext";
import { useWebSocket } from "../../context/WebSocketContext";
import ReferralSettings from "./ReferralSettings";
import PaymentGatewaySettings from "./PaymentGatewaySettings";

export default function Settings() {
  const { user, tokens } = useAuth();
  const { 
    notificationSettings, 
    updateNotificationSettings 
  } = useWebSocket();
  const [activeTab, setActiveTab] = useState("commission");
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");
  const [loading, setLoading] = useState(true);

  // Check if user is admin
  const isAdmin = user?.roleId === 1;

  useEffect(() => {
    // Fetch existing settings
    const fetchSettings = async () => {
      try {
        setLoading(true);
        
        // Get commission start time setting
        const startTimeResponse = await settingsApi.getSettings(
          tokens.access.token, 
          user.id, 
          "commissionConversionStartTime"
        );
        if (startTimeResponse.status === 200 && startTimeResponse.data) {
            console.log(startTimeResponse);
          setStartTime(startTimeResponse.data.value || "00:00");
        }
        
        // Get commission end time setting
        const endTimeResponse = await settingsApi.getSettings(
          tokens.access.token, 
          user.id, 
          "commissionConversionEndTime"
        );
        
        if (endTimeResponse.status === 200 && endTimeResponse.data) {
          setEndTime(endTimeResponse.data.value || "23:59");
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isAdmin) {
      fetchSettings();
    }
  }, [tokens.access.token, user.id, isAdmin]);

  const handleSaveSettings = async () => {
    if (!isAdmin) {
      toast.error("Only administrators can change these settings");
      return;
    }

    try {
      // Save start time
      const startTimeResponse = await settingsApi.settings(
        tokens.access.token,
        user.id,
        "commissionConversionStartTime",
        startTime
      );

      // Save end time
      const endTimeResponse = await settingsApi.settings(
        tokens.access.token,
        user.id,
        "commissionConversionEndTime",
        endTime
      );

      if (startTimeResponse.status === 201 && endTimeResponse.status === 201) {
        toast.success("Settings saved successfully");
      } else {
        toast.error("Failed to save some settings");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    }
  };

  const renderCommissionSettings = () => {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600 mb-6">
          Set the allowed time window for commission conversion to wallet
        </p>
        
        {/* Start Time */}
        <div>
          <Label className="block text-sm font-medium text-gray-700 mb-1">Start Time</Label>
          <div className="relative">
            <TextInput
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full"
              required
            />
            <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
          </div>
        </div>

        {/* End Time */}
        <div>
          <Label className="block text-sm font-medium text-gray-700 mb-1">End Time (Default: 12 Midnight)</Label>
          <div className="relative">
            <TextInput
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full"
              required
            />
            <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
          </div>
        </div>

        {/* Save Button */}
        <button
          type="button"
          onClick={handleSaveSettings}
          disabled={loading}
          className="w-full mt-6 p-2.5 bg-[#65758B] hover:bg-[#566275] text-white rounded-md font-medium"
        >
          {loading ? "Loading..." : "Save Settings"}
        </button>
      </div>
    );
  };

  const renderReferralSettings = () => {
    return (
      <div className="space-y-4">
        <ReferralSettings />
      </div>
    );
  };

  const renderNotificationSettings = () => {
    return (
      <div className="space-y-6">
        <p className="text-sm text-gray-600 mb-4">
          Manage your in-app notification preferences
        </p>

        {/* Notification Types */}
        <div className="space-y-4">
          <h3 className="font-medium text-gray-800">Notification Types</h3>
          
          {/* Telegram Messages */}
          <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-[#0088cc]/10">
                <MessageCircle className="w-5 h-5 text-[#0088cc]" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Telegram Messages</p>
                <p className="text-xs text-gray-500">Get notified when users send messages via Telegram</p>
              </div>
            </div>
            <ToggleSwitch
              checked={notificationSettings.telegramMessages}
              onChange={(checked) => updateNotificationSettings({ telegramMessages: checked })}
              color="blue"
            />
          </div>

          {/* Support Tickets */}
          <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-[#0E64D1]/10">
                <Smartphone className="w-5 h-5 text-[#0E64D1]" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Support Tickets</p>
                <p className="text-xs text-gray-500">Get notified about new support tickets and messages</p>
              </div>
            </div>
            <ToggleSwitch
              checked={notificationSettings.supportTickets}
              onChange={(checked) => updateNotificationSettings({ supportTickets: checked })}
              color="blue"
            />
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> These settings control in-app toast notifications that appear while you're using the app.
          </p>
        </div>
      </div>
    );
  };

  return (
    <LeftToRightPageWrapper>
      <div className="min-h-screen pb-16">
        <HeaderWithIcon title="Settings" />

        {!isAdmin ? (
          <div className="p-4 text-center text-red-500">
            Only administrators can access this page
          </div>
        ) : (
          <div className="p-4">
            {/* Tabs - scrollable on mobile */}
            <div className="flex overflow-x-auto border-b mb-6 hide-scrollbar">
              <button
                onClick={() => setActiveTab("notifications")}
                className={`py-2 px-4 font-medium text-sm whitespace-nowrap ${
                  activeTab === "notifications"
                    ? "border-b-2 border-[#65758B] text-[#65758B]"
                    : "text-gray-500"
                }`}
              >
                Notifications
              </button>
              <button
                onClick={() => setActiveTab("commission")}
                className={`py-2 px-4 font-medium text-sm whitespace-nowrap ${
                  activeTab === "commission"
                    ? "border-b-2 border-[#65758B] text-[#65758B]"
                    : "text-gray-500"
                }`}
              >
                Commission
              </button>
              <button
                onClick={() => setActiveTab("referral")}
                className={`py-2 px-4 font-medium text-sm whitespace-nowrap ${
                  activeTab === "referral"
                    ? "border-b-2 border-[#65758B] text-[#65758B]"
                    : "text-gray-500"
                }`}
              >
                Referral
              </button>
              <button
                onClick={() => setActiveTab("paymentGateway")}
                className={`py-2 px-4 font-medium text-sm whitespace-nowrap ${
                  activeTab === "paymentGateway"
                    ? "border-b-2 border-[#65758B] text-[#65758B]"
                    : "text-gray-500"
                }`}
              >
                Payment
              </button>
            </div>

            {/* Tab content */}
            {activeTab === "notifications"
              ? renderNotificationSettings()
              : activeTab === "commission"
              ? renderCommissionSettings()
              : activeTab === "referral"
              ? renderReferralSettings()
              : <PaymentGatewaySettings />}
          </div>
        )}
      </div>
    </LeftToRightPageWrapper>
  );
}
